import {
  collection, doc, getDoc, getDocs, query, where, orderBy, limit, onSnapshot,
  serverTimestamp, setDoc, addDoc, updateDoc, type DocumentData, type QueryConstraint,
} from 'firebase/firestore';
import { db } from './firebase';
import type {
  AuditDoc, CampaignDoc, CampaignHostDoc, Organization, UserProfile, QuestionnaireDoc, Invitation, ReleaseDoc, ExportDoc, ReleaseOs, ReleaseArch,
  Ticket, TicketMessage, TicketStatus,
} from '@/types/models';
import { pickRelease, releaseKey } from './releaseBinary';

// ─── User profile ─────────────────────────────────────────────
export async function getUserProfile(uid: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? ({ uid, ...snap.data() } as UserProfile) : null;
}

export async function ensureUserProfile(uid: string, email: string, displayName: string | null): Promise<void> {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      email,
      displayName: displayName ?? email.split('@')[0],
      orgId: null,
      role: 'org_member',
      locale: 'fr',
      createdAt: serverTimestamp(),
    });
  }
}

export interface AssumedOrgSignal {
  assumedOrgId: string | null;
  updatedAt?: import('firebase/firestore').Timestamp;
}

/**
 * Watch the per-user "assumedOrg" signal doc. Written server-side whenever an
 * admin assumes / releases acting as an org. Lets every open session of that
 * admin refresh its ID token so the new `assumedOrgId` claim propagates without
 * forcing a re-login. `cb` receives `null` when the doc does not exist (no
 * assumed org).
 */
export function watchAssumedOrgSignal(uid: string, cb: (sig: AssumedOrgSignal | null) => void): () => void {
  return onSnapshot(
    doc(db, 'users', uid, 'sessions', 'assumedOrg'),
    s => cb(s.exists() ? (s.data() as AssumedOrgSignal) : null),
    () => cb(null),
  );
}

// ─── Organizations ────────────────────────────────────────────
export async function getOrg(orgId: string): Promise<Organization | null> {
  const snap = await getDoc(doc(db, 'organizations', orgId));
  return snap.exists() ? ({ id: orgId, ...snap.data() } as Organization) : null;
}

export async function listAllOrgs(): Promise<Organization[]> {
  const snap = await getDocs(query(collection(db, 'organizations'), orderBy('displayName')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Organization));
}

// ─── Audits ───────────────────────────────────────────────────
export function watchAuditsForOrg(
  orgId: string,
  cb: (audits: AuditDoc[]) => void,
  opts: { limit?: number } = {},
): () => void {
  const constraints: QueryConstraint[] = [
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc'),
  ];
  if (opts.limit) constraints.push(limit(opts.limit));
  const q = query(collection(db, 'audits'), ...constraints);
  return onSnapshot(q, snap => {
    cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as AuditDoc)));
  });
}

export function watchAudit(auditId: string, cb: (a: AuditDoc | null) => void): () => void {
  return onSnapshot(doc(db, 'audits', auditId), s =>
    cb(s.exists() ? ({ id: s.id, ...s.data() } as AuditDoc) : null),
  );
}

export async function getAllAuditModules(auditId: string): Promise<Record<string, DocumentData>> {
  const snap = await getDocs(collection(db, 'audits', auditId, 'modules'));
  const out: Record<string, DocumentData> = {};
  snap.forEach(d => { out[d.id] = d.data(); });
  return out;
}

/** Update audit metadata. Futur implementation if the admin want to update the audit metadata like add notes, tags, etc. */
// export async function updateAuditMeta(auditId: string, patch: Partial<AuditDoc>): Promise<void> {
//   await updateDoc(doc(db, 'audits', auditId), { ...patch, updatedAt: serverTimestamp() });
// }

// ─── Questionnaires ───────────────────────────────────────────
export function watchQuestionnaire(
  id: string,
  cb: (q: QuestionnaireDoc | null) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'questionnaires', id),
    s => cb(s.exists() ? ({ id: s.id, ...s.data() } as QuestionnaireDoc) : null),
    err => onError?.(err as Error),
  );
}

export async function listOrgQuestionnaires(orgId: string): Promise<QuestionnaireDoc[]> {
  const snap = await getDocs(query(
    collection(db, 'questionnaires'),
    where('orgId', '==', orgId),
    orderBy('updatedAt', 'desc'),
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as QuestionnaireDoc));
}

/** True when the org has at least one submitted AI Readiness questionnaire. */
export async function orgHasSubmittedQuestionnaire(orgId: string): Promise<boolean> {
  const snap = await getDocs(query(
    collection(db, 'questionnaires'),
    where('orgId', '==', orgId),
    where('status', '==', 'submitted'),
    limit(1),
  ));
  return !snap.empty;
}

/**
 * Questionnaire rattaché à un audit (CPL-03/04) : celui dont l'identifiant est
 * posé sur l'AuditDoc à la création, sinon le dernier questionnaire SOUMIS de
 * l'organisation. `null` quand l'org n'en a aucun — l'onglet « Déclaré vs
 * observé » le dit alors, au lieu de rapprocher l'audit d'un brouillon vide.
 */
export async function getAuditQuestionnaire(
  orgId: string, questionnaireId?: string | null,
): Promise<QuestionnaireDoc | null> {
  if (questionnaireId) {
    const snap = await getDoc(doc(db, 'questionnaires', questionnaireId));
    if (snap.exists()) return { id: snap.id, ...snap.data() } as QuestionnaireDoc;
  }
  // Réutilise l'index (orgId, updatedAt desc) de `listOrgQuestionnaires` : le
  // filtre « soumis » se fait ici plutôt qu'en ajoutant un index composite.
  const list = await listOrgQuestionnaires(orgId);
  return list.find(q => q.status === 'submitted') ?? null;
}

export async function createQuestionnaire(
  orgId: string,
  filledByUid: string,
  onBehalf: boolean,
): Promise<string> {
  const ref = await addDoc(collection(db, 'questionnaires'), {
    orgId,
    filledByUid,
    filledOnBehalfOfClient: onBehalf,
    status: 'draft',
    data: {},
    locale: 'fr',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function saveQuestionnaireData(id: string, data: Record<string, unknown>): Promise<void> {
  await updateDoc(doc(db, 'questionnaires', id), { data, updatedAt: serverTimestamp() });
}

export async function submitQuestionnaire(id: string): Promise<void> {
  await updateDoc(doc(db, 'questionnaires', id), {
    status: 'submitted',
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// ─── Invitations ──────────────────────────────────────────────
export function watchOrgInvitations(orgId: string, cb: (inv: Invitation[]) => void): () => void {
  const q = query(
    collection(db, 'invitations'),
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, s => cb(s.docs.map(d => ({ id: d.id, ...d.data() } as Invitation))));
}

// ─── Members ─────────────────────────────────────────────────
export interface OrgMember {
  uid: string;
  role: 'org_admin' | 'org_member';
  joinedAt?: import('firebase/firestore').Timestamp;
  email?: string;
  displayName?: string;
}

export function watchOrgMembers(orgId: string, cb: (m: OrgMember[]) => void): () => void {
  const ref = collection(db, 'organizations', orgId, 'members');
  return onSnapshot(ref, async snap => {
    const members: OrgMember[] = [];
    for (const d of snap.docs) {
      const data = d.data() as Omit<OrgMember, 'uid' | 'email' | 'displayName'>;
      const uref = doc(db, 'users', d.id);
      const usnap = await getDoc(uref);
      const u = usnap.exists() ? usnap.data() : {};
      members.push({
        uid: d.id,
        role: data.role,
        joinedAt: data.joinedAt,
        email: u.email,
        displayName: u.displayName,
      });
    }
    cb(members);
  });
}

// ─── Releases ─────────────────────────────────────────────────
/**
 * Dernière release par combinaison `os/arch/variant` (clé {@link releaseKey})
 * pour un canal — première occurrence gagnante, les docs étant triés du plus
 * récent au plus ancien. Les docs legacy sans `arch`/`variant` valent
 * x86_64 / onefile.
 */
export async function listLatestReleases(
  channel: 'stable' | 'beta' = 'stable',
): Promise<Record<string, ReleaseDoc>> {
  const snap = await getDocs(query(
    collection(db, 'releases'),
    where('channel', '==', channel),
    orderBy('publishedAt', 'desc'),
    limit(60),
  ));
  const out: Record<string, ReleaseDoc> = {};
  for (const d of snap.docs) {
    const r = { id: d.id, ...d.data() } as ReleaseDoc;
    const key = releaseKey(r.os, r.arch, r.variant);
    if (!out[key]) out[key] = r;
  }
  return out;
}

/** Dernière release onefile pour `os`/`arch` (repli sur un binaire `universal`). */
export async function getLatestRelease(
  os: ReleaseOs, arch: ReleaseArch, channel: 'stable' | 'beta' = 'stable',
): Promise<ReleaseDoc | null> {
  return pickRelease(Object.values(await listLatestReleases(channel)), os, arch);
}

// ─── Exports ──────────────────────────────────────────────────
export function watchAuditExports(auditId: string, cb: (e: ExportDoc[]) => void): () => void {
  const q = query(
    collection(db, 'audits', auditId, 'exports'),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(q, s => cb(s.docs.map(d => ({ id: d.id, ...d.data() } as ExportDoc))));
}

// ─── Documents (generic org documents: scopes, uploaded reports, etc.) ───────
import type { DocumentDoc } from '@/types/models';

export function watchOrgDocuments(
  orgId: string,
  cb: (docs: DocumentDoc[]) => void,
): () => void {
  const q = query(
    collection(db, 'documents'),
    where('orgId', '==', orgId),
    where('publishedToClient', '==', true),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as DocumentDoc))),
    () => cb([]),
  );
}

// Admin: watch all documents for an org (including unpublished)
export function watchOrgDocumentsAdmin(
  orgId: string,
  cb: (docs: DocumentDoc[]) => void,
): () => void {
  const q = query(
    collection(db, 'documents'),
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as DocumentDoc))),
    () => cb([]),
  );
}

// ─── Support Tickets ──────────────────────────────────────────

/** Watch all tickets for an org (all members see all org tickets). */
export function watchTicketsForOrg(
  orgId: string,
  cb: (tickets: Ticket[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets'),
    where('orgId', '==', orgId),
    orderBy('updatedAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket))),
    () => cb([]),
  );
}

/** Watch a single ticket document. */
export function watchTicket(
  ticketId: string,
  cb: (ticket: Ticket | null) => void,
): () => void {
  return onSnapshot(
    doc(db, 'tickets', ticketId),
    s => cb(s.exists() ? ({ id: s.id, ...s.data() } as Ticket) : null),
  );
}

/** Watch all messages of a ticket in real-time (ordered by createdAt). */
export function watchTicketMessages(
  ticketId: string,
  cb: (messages: TicketMessage[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets', ticketId, 'messages'),
    orderBy('createdAt', 'asc'),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as TicketMessage))),
    () => cb([]),
  );
}

/** Admin: watch ALL tickets across all orgs, ordered by updatedAt. */
export function watchAllTickets(
  cb: (tickets: Ticket[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets'),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket))),
    () => cb([]),
  );
}

/** Admin: watch tickets filtered by status across all orgs. */
export function watchAllTicketsByStatus(
  status: TicketStatus,
  cb: (tickets: Ticket[]) => void,
): () => void {
  const q = query(
    collection(db, 'tickets'),
    where('status', '==', status),
    orderBy('updatedAt', 'desc'),
    limit(200),
  );
  return onSnapshot(
    q,
    snap => cb(snap.docs.map(d => ({ id: d.id, ...d.data() } as Ticket))),
    () => cb([]),
  );
}
// ─── Campagnes ────────────────────────────────────────────────
// Lecture seule : tout est écrit côté fonctions (createCampaign,
// redeemAuditCode, onAuditUploaded, closeCampaign). Le code d'appairage n'est
// PAS dans ces documents — il n'existe en clair nulle part côté serveur.

/** Campagnes de l'organisation, de la plus récente à la plus ancienne. */
export async function listCampaigns(orgId: string, opts: { limit?: number } = {}): Promise<CampaignDoc[]> {
  const constraints: QueryConstraint[] = [
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc'),
  ];
  if (opts.limit) constraints.push(limit(opts.limit));
  const snap = await getDocs(query(collection(db, 'campaigns'), ...constraints));
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as CampaignDoc));
}

/** Suivi temps réel d'une campagne (statut, consolidé, facturation). */
export function watchCampaign(
  campaignId: string,
  cb: (c: CampaignDoc | null) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, 'campaigns', campaignId),
    s => cb(s.exists() ? ({ id: s.id, ...s.data() } as CampaignDoc) : null),
    err => onError?.(err as Error),
  );
}

/**
 * Suivi temps réel machine par machine (`campaigns/{id}/hosts`), trié par
 * slug : c'est ce qui fait avancer les lignes « attendue → reçue → en analyse
 * → prête » sans rechargement.
 */
export function watchCampaignHosts(
  campaignId: string,
  cb: (hosts: CampaignHostDoc[]) => void,
  onError?: (err: Error) => void,
): () => void {
  return onSnapshot(
    collection(db, 'campaigns', campaignId, 'hosts'),
    snap => cb(
      snap.docs
        .map(d => ({ slug: d.id, ...d.data() } as CampaignHostDoc))
        .sort((a, b) => a.slug.localeCompare(b.slug)),
    ),
    err => onError?.(err as Error),
  );
}
